import {
  IBank,
  IBankTableColumns,
  useDeleteBankMutation,
  useEditBankMutation,
} from '@entities/bank';
import { ModalDeleteEntity } from '@features/modal-entity-delete';
import { ModalEditEntityV2 } from '@features/modal-entity-edit';
import { ModalViewEntityV2 } from '@features/modal-entity-view';
import { ITableActionsConfig, TableActionBtns } from '@features/table-action-btns';

import { NewBankModal } from '../components/NewBank';
import { NewBankSettings } from '../components/NewBankSettings';

interface IProps {
  refetch?: () => void;
}

export const useTableSettings = ({ refetch }: IProps) => {
  const [deleteBank] = useDeleteBankMutation();

  // THead settings
  const columnTitles: IBankTableColumns = {
    name: 'Name',
    holder_name: 'Holder Name',
    bank_validator_name: 'Validator',
    created_at: 'Created At',
    updated_at: 'Updated At',
    actions: 'Actions',
  };

  // TBody settings
  const renderRow = (key: keyof IBankTableColumns, bank: IBank) => {
    switch (key) {
      case 'actions': {
        const bankViewEntity = bank as IBank &
          Record<'form_id' | 'withdraw_first_step' | 'deposit_first_step', unknown>;
        const renderViewKeys: Array<keyof typeof bankViewEntity> = [
          'id',
          'form_id',
          'name',
          'bank_validator_name',
          'holder_name',
          'withdraw_first_step',
          'deposit_first_step',
          'created_at',
          'updated_at',
        ];

        const bankEditEntity = bank as IBank &
          Record<'form_id' | 'withdraw_first_step' | 'deposit_first_step', unknown>;
        const renderEditKeys: Array<keyof typeof bankEditEntity> = [
          'form_id',
          'name',
          'holder_name',
          'withdraw_first_step',
          'deposit_first_step',
        ];

        const actionBtnsConfig: ITableActionsConfig = {
          edit: {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <ModalEditEntityV2
                title={`Edit Bank ${bank.name}`}
                entity={bankEditEntity}
                renderKeys={renderEditKeys}
                useEditEntityMutation={useEditBankMutation}
                refetch={refetch}
                onTransformFormData={formData => {
                  const data = Object.fromEntries(formData.entries());
                  if (data.form_id === '') data.form_id = null;
                  return data;
                }}
              />
            ),
            popupSize: 'md',
          },
          'edit-settings': {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <NewBankSettings
                isEdit={true}
                initialMethods={bank.settings.bank_wallet_config}
                parent_id={bank.id}
                parent_name={bank.name}
                refetch={refetch}
              />
            ),
            popupSize: 'md',
          },
          view: {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <ModalViewEntityV2
                entity={bankViewEntity}
                title={`View Bank ${bank.name}`}
                renderKeys={renderViewKeys}
              />
            ),
            popupSize: 'md',
          },
          delete: {
            isEnabled: true,
            isVisible: true,
            popupElem: (
              <ModalDeleteEntity
                id={bank.id}
                entityName={bank.name}
                message='Do you want to delete this bank?'
                deleteEntity={deleteBank}
                refetch={refetch}
              />
            ),
            popupSize: 'sm',
          },
        };

        return <TableActionBtns config={actionBtnsConfig} />;
      }

      default:
        return bank[key];
    }
  };

  const NewBank = () => <NewBankModal refetch={refetch} />;
  return {
    columnTitles,
    renderRow,
    NewBank,
  };
};
